import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, HStack, Text, IconButton, VStack } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import type { AudioPlaylistTrackDto } from '../types';
import { formatTrackDuration } from '../utils';
import { PreviewButton } from './AudioPreview';

interface SortableTrackListProps {
  tracks: AudioPlaylistTrackDto[];
  onReorder: (orderedTrackIds: string[]) => void;
  onRemove: (trackId: string) => void;
}

function SortableRow({
  track,
  index,
  onRemove,
}: {
  track: AudioPlaylistTrackDto;
  index: number;
  onRemove: (trackId: string) => void;
}) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: track.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <HStack
      ref={setNodeRef}
      style={style}
      py={2.5}
      px={2}
      spacing={3}
      bg="white"
      _hover={{ bg: 'gray.50' }}
      borderBottomWidth="1px"
      borderColor="gray.100"
    >
      <Box
        {...attributes}
        {...listeners}
        cursor="grab"
        color="gray.300"
        fontSize="lg"
        lineHeight={1}
        px={1}
        aria-label={t('playlists.dragHandle')}
        _hover={{ color: 'gray.500' }}
      >
        ⠿
      </Box>
      <Text w="24px" textAlign="right" color="gray.400" fontSize="sm" flexShrink={0}>
        {index + 1}
      </Text>
      <Box flex={1} minW={0}>
        <Text fontWeight="500" color="gray.800" noOfLines={1}>
          {track.title}
        </Text>
        {track.artist && (
          <Text fontSize="sm" color="gray.500" noOfLines={1}>
            {track.artist}
          </Text>
        )}
      </Box>
      <PreviewButton id={track.id} resolveUrl={() => Promise.resolve(track.streamUrl)} size="xs" />
      <Text fontSize="sm" color="gray.400" flexShrink={0}>
        {formatTrackDuration(track.durationSeconds)}
      </Text>
      <IconButton
        aria-label={t('playlists.removeTrack')}
        icon={<Text fontSize="sm">✕</Text>}
        size="xs"
        variant="ghost"
        color="gray.400"
        _hover={{ color: 'red.500', bg: 'red.50' }}
        onClick={() => onRemove(track.id)}
      />
    </HStack>
  );
}

export function SortableTrackList({ tracks, onReorder, onRemove }: SortableTrackListProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const ids = tracks.map((t) => t.id);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = ids.indexOf(active.id as string);
    const newIndex = ids.indexOf(over.id as string);
    if (oldIndex < 0 || newIndex < 0) return;
    onReorder(arrayMove(ids, oldIndex, newIndex));
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <VStack align="stretch" spacing={0}>
          {tracks.map((track, idx) => (
            <SortableRow key={track.id} track={track} index={idx} onRemove={onRemove} />
          ))}
        </VStack>
      </SortableContext>
    </DndContext>
  );
}

export default SortableTrackList;
